import { Box, Flex } from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ChatHeader from '../components/ChatHeader'
import { ChatInterface } from '../components/ChatInterface/ChatInterface'
import { SidebarToggleButton } from '../components/Sidebar'
import ChatSidebar from '../components/Sidebar/ChatSidebar'

import { useQueryClient } from '@tanstack/react-query'
import { useChatLogic } from '../hooks/chat/useChatLogic'
import useMobile from '../hooks/utility/useMobile'

const ChatHomePage = () => {
  const isMobile = useMobile()
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true) // Default open for desktop
  const navigate = useNavigate()
  const { agentId } = useParams()
  const queryClient = useQueryClient()



  // Callback for when a new chat is established (first message sent successfully)
  const handleNewChatEstablished = useCallback(
    (newChatId: string) => {
      // Update URL smoothly without page refresh
      const newUrl = `/a/${agentId}/c/${newChatId}`
      window.history.replaceState(null, '', newUrl)
      queryClient.invalidateQueries({ queryKey: ['chatSummaries'] })
    },
    [agentId, queryClient],
  )

  const {
    conversation,
    clearConversation,
    clearConversationAndRegenerateChatId,
    isTyping,
    setIsTyping,
    sendMessage,
    handleRetry,
    toggleResponseMinimized,
    chatContainerRef,
    isInputDisabled,
    hasDocumentsInConversation,
    currentPhase,
  } = useChatLogic({
    onChatEstablished: handleNewChatEstablished,
    isNewChatSession: true, // Explicitly mark this as a new chat session page
    agentId: agentId, // Pass current agent ID to track changes
  })

  const handleSelectAgent = (selectedAgentId: string) => {
    if (selectedAgentId !== agentId) {
      clearConversationAndRegenerateChatId()

      navigate(`/a/${selectedAgentId}`)
    }
  }

  const handleSelectChat = (chatId: string, chatAgentId?: string) => {

    // Clear conversation when switching to a different chat
    clearConversation()

    if (chatAgentId) {
      // Navigate to agent-specific chat route
      navigate(`/a/${chatAgentId}/c/${chatId}`)
    } else {
      // Fallback for legacy routes without agent
      navigate(`/c/${chatId}`)
    }
  }

  const handleNewChat = () => {
    clearConversationAndRegenerateChatId()

    // Navigate to agent-specific home (agentId is always available now)
    navigate(`/a/${agentId}`)
  }

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  return (
    <Flex width='100vw' height='100vh' flexDirection='row' position='relative'>
      {/* Sidebar - Always render, but it slides out when closed */}
      <ChatSidebar
        onNewChat={handleNewChat}
        isLoadingDetails={false} // No specific chat details are loaded on the home page
        selectedConversationId={undefined} // No chat is "selected" on the home page initially
        onSelectConversation={handleSelectChat}
        onSelectAgent={handleSelectAgent}
        isSidebarOpenExternal={isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onClearConversation={clearConversation}
      />

      {/* Floating toggle button when sidebar is closed */}
      {!isSidebarOpen && (
        <SidebarToggleButton
          isOpen={isSidebarOpen}
          onToggle={handleSidebarToggle}
          isFloating={true}
        />
      )}

      {/* Main content area */}
      <Flex
        flex='1'
        h='100vh'
        flexDirection='column'
        minW='0'
        ml={isSidebarOpen && !isMobile ? '300px' : '0'}
        transition='margin-left 0.3s ease-in-out'
      >
        <ChatHeader />
        <Box flex='1' minHeight='0' overflowY='hidden'>
          <ChatInterface
            conversation={conversation}
            chatContainerRef={chatContainerRef}
            isInputDisabled={isInputDisabled}
            isTyping={isTyping} // Pass isTyping for QuestionInput
            onSend={sendMessage}
            onRetry={handleRetry} // Pass the new handleRetry from the hook
            onTypingChange={setIsTyping} // Pass setIsTyping for onTypingChange
            hasDocumentsInConversation={hasDocumentsInConversation}
            onToggleResponseMinimized={toggleResponseMinimized}
            currentPhase={currentPhase}
          />
        </Box>
      </Flex>

      {/* Mobile overlay backdrop */}
      {isMobile && isSidebarOpen && (
        <Box
          position='fixed'
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg='blackAlpha.600'
          zIndex={999}
          onClick={handleSidebarToggle}
        />
      )}

    </Flex>
  )
}

export default ChatHomePage
